import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../contexts/StoreContext';
import notificationService from '../../services/notification.service';
import { Notification } from '../../types/notification.types';
import { notificationHref } from '../../utils/notificationDisplay';
import { isNotificationForStore } from '../../utils/notificationStoreScope';
import NotificationItem from '../NotificationItem/NotificationItem';
import Icon from '../Icon/Icon';
import './NotificationBell.scss';

interface NotificationBellProps {
  newNotification?: Notification | null;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ newNotification }) => {
  const { currentStore } = useStore();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [panelPosition, setPanelPosition] = useState({ top: 0, right: 0 });
  const [isBellAnimating, setIsBellAnimating] = useState(false);

  const loadNotifications = async () => {
    if (!currentStore) return;
    try {
      setLoading(true);
      const [notifs, count] = await Promise.all([
        notificationService.getNotifications(currentStore.id, { limit: 20 }),
        notificationService.getUnreadCount(currentStore.id)
      ]);
      setNotifications(notifs);
      setUnreadCount(count);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentStore && isOpen) {
      void loadNotifications();
    }
  }, [currentStore, isOpen]);

  useEffect(() => {
    if (currentStore && isOpen && unreadCount > 0) {
      const timer = setTimeout(async () => {
        try {
          await notificationService.markAllAsRead(currentStore.id);
          setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
          setUnreadCount(0);
        } catch (error) {
          console.error('Error marking notifications as read:', error);
        }
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [currentStore, isOpen, unreadCount]);

  useEffect(() => {
    if (currentStore) {
      notificationService.getUnreadCount(currentStore.id).then(setUnreadCount).catch(console.error);
    }
  }, [currentStore]);

  useEffect(() => {
    if (!newNotification || !isNotificationForStore(newNotification, currentStore?.id)) return;
    setNotifications(prev => [newNotification, ...prev]);
    setUnreadCount(prev => prev + 1);
    setIsBellAnimating(true);
    const timer = setTimeout(() => setIsBellAnimating(false), 1200);
    return () => clearTimeout(timer);
  }, [newNotification, currentStore?.id]);

  useEffect(() => {
    const updatePosition = () => {
      if (isOpen && buttonRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        setPanelPosition({
          top: buttonRect.bottom + 8,
          right: window.innerWidth - buttonRect.right
        });
      }
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        buttonRef.current &&
        !panelRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = async (notification: Notification) => {
    if (currentStore && !notification.is_read) {
      try {
        await notificationService.markAsRead(currentStore.id, notification.id);
        setNotifications(prev =>
          prev.map(n => (n.id === notification.id ? { ...n, is_read: true } : n))
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Error marking as read:', error);
      }
    }
    const href = notificationHref(notification);
    setIsOpen(false);
    if (href) navigate(href);
  };

  const handleDelete = async (notificationId: number) => {
    if (!currentStore) return;
    try {
      await notificationService.deleteNotification(currentStore.id, notificationId);
      const notification = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (notification && !notification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  return (
    <div className="notification-bell">
      <button
        ref={buttonRef}
        className={`notification-bell__button${isBellAnimating ? ' notification-bell__button--ring' : ''}`}
        onClick={() => setIsOpen(open => !open)}
        title="Notifications"
        aria-expanded={isOpen}
      >
        <Icon name="bell" size={15} />
        {unreadCount > 0 ? (
          <span className="notification-bell__badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        ) : null}
      </button>

      {isOpen
        ? createPortal(
            <div
              ref={panelRef}
              className="notification-bell__panel"
              style={{ top: panelPosition.top, right: panelPosition.right }}
            >
              <div className="notification-bell__header">
                <div>
                  <p>Activité</p>
                  <h3>Notifications</h3>
                </div>
                <button
                  type="button"
                  className="notification-bell__close"
                  onClick={() => setIsOpen(false)}
                  aria-label="Fermer"
                >
                  <Icon name="x" size={14} />
                </button>
              </div>

              <div className="notification-bell__list">
                {loading ? (
                  <div className="notification-bell__empty">
                    <Icon name="loader" size={18} />
                    <p>Chargement</p>
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="notification-bell__empty">
                    <Icon name="bell" size={22} />
                    <p>Aucune</p>
                  </div>
                ) : (
                  notifications.map(notification => (
                    <NotificationItem
                      key={notification.id}
                      compact
                      notification={notification}
                      onSelect={handleSelect}
                      onDelete={handleDelete}
                    />
                  ))
                )}
              </div>

              <div className="notification-bell__footer">
                <button
                  type="button"
                  className="notification-bell__footer-btn"
                  onClick={() => {
                    setIsOpen(false);
                    navigate('/notifications');
                  }}
                >
                  Tout voir
                </button>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default NotificationBell;
